import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProductsService, ProductVersionsService, RoadmapItemsService, ProductDocumentsService, ProductGalleryService, ProductAuditLogsService } from '../../services/products/products.service';
import { ProductModel, ProductDetailsModel, ProductVersionModel, ProductRoadmapItemModel, ProductDocumentModel, ProductGalleryModel, ProductHistoryModel } from '../../types/models/product';

export function useProducts(params?: Record<string, any>) {
  return useQuery({
    queryKey: ['products', 'list', params],
    queryFn: async () => ProductsService.list(params),
  });
}

export function useProductDetails(id: string | number) {
  return useQuery({
    queryKey: ['products', 'details', id],
    queryFn: async () => ProductsService.getById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

export function useProductFullDetails(id: string | number) {
  return useQuery({
    queryKey: ['products', 'full-details', id],
    queryFn: async () => ProductsService.getDetails(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => ProductsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: any }) => ProductsService.update(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'details', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['products', 'full-details', variables.id] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => ProductsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useProductVersions(productId: string | number) {
  return useQuery({
    queryKey: ['product-versions', 'list', productId],
    queryFn: async () => ProductVersionsService.listByProduct(productId),
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateProductVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { productId: string; versionTag: string; changelog?: string; releasedAt?: string }) =>
      ProductVersionsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-versions'] });
    },
  });
}

export function useUpdateProductVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: any }) =>
      ProductVersionsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-versions'] });
    },
  });
}

export function useDeleteProductVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => ProductVersionsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-versions'] });
    },
  });
}

export function useRoadmapItems(productId: string | number) {
  return useQuery({
    queryKey: ['roadmap-items', 'list', productId],
    queryFn: async () => RoadmapItemsService.listByProduct(productId),
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { productId: string; title: string; status?: string; priority?: number; plannedRelease?: string }) =>
      RoadmapItemsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap-items'] });
    },
  });
}

export function useUpdateRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: any }) =>
      RoadmapItemsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap-items'] });
    },
  });
}

export function useDeleteRoadmapItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => RoadmapItemsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roadmap-items'] });
    },
  });
}

export function useProductDocuments(productId: string | number) {
  return useQuery({
    queryKey: ['product-documents', 'list', productId],
    queryFn: async () => ProductDocumentsService.list(productId),
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useUploadProductDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, file, data }: { productId: string | number; file: File; data?: { title?: string; folder?: string; description?: string } }) =>
      ProductDocumentsService.upload(productId, file, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-documents'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'full-details', variables.productId] });
    },
  });
}

export function useDeleteProductDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, documentId }: { productId: string | number; documentId: string | number }) =>
      ProductDocumentsService.delete(productId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-documents'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'full-details'] });
    },
  });
}

export function useProductGallery(productId: string | number) {
  return useQuery({
    queryKey: ['product-gallery', 'list', productId],
    queryFn: async () => ProductGalleryService.list(productId),
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
  });
}

export function useUploadProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, file, data }: { productId: string | number; file: File; data?: { alt?: string; isPrimary?: boolean } }) =>
      ProductGalleryService.upload(productId, file, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['product-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'full-details', variables.productId] });
    },
  });
}

export function useDeleteProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, imageId }: { productId: string | number; imageId: string | number }) =>
      ProductGalleryService.delete(productId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-gallery'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'full-details'] });
    },
  });
}

export function useProductAuditLogs(productId: string | number) {
  return useQuery({
    queryKey: ['product-audit-logs', 'list', productId],
    queryFn: async () => ProductAuditLogsService.list(productId),
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
  });
}
